import {
  isVolumeMeasurement,
  MeasurementsCapabilityProvider,
  VolumeMeasurementType,
  VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX,
} from '@trimble/web3d-providers';
import { FitToViewBaseCapabilityCoordinator } from './fit-to-view-base-capability-coordinator';
import { NavigationalCapabilityBase } from '@trimble/web3d-capabilities';
import {
  Box3,
  Matrix4,
  Quaternion,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import { Constructor } from '../capability-registry/capability-registry-types';

export const FIT_TO_VIEW_TIGHTNESS = 0.9;

function getVolumeBoundaryBox(
  measurement: VolumeMeasurementType
): Box3 | undefined {
  const COORDINATE_INDEX_Z = 2;
  const ring = (
    measurement.parameters?.boundaryInclusive as
      | { coordinates?: number[][][] }
      | undefined
  )?.coordinates?.[0];
  if (!ring?.length) return undefined;

  const points = ring.map(
    (c) =>
      new Vector3(
        c[0],
        c[1],
        typeof c[COORDINATE_INDEX_Z] === 'number' ? c[COORDINATE_INDEX_Z] : 0
      )
  );
  return new Box3().setFromPoints(points);
}

// Filter-based volumes have no heatmap tileset model until WEKA-584.
function volumeUsesFilter(measurement: VolumeMeasurementType): boolean {
  const { fromFilterId, toFilterId } = measurement.parameters ?? {};
  return Boolean(fromFilterId) || Boolean(toFilterId);
}

export class FitToViewMeasurementCapabilityCoordinator extends FitToViewBaseCapabilityCoordinator {
  public async fitMeasurementToView<T extends NavigationalCapabilityBase>(
    measurementId: string,
    capabilities?: Constructor<T>[]
  ): Promise<void> {
    const provider = this.registry.get(MeasurementsCapabilityProvider)();
    if (!provider) return;

    const viewers = this.getViewersToFit(capabilities);
    if (viewers.length === 0) return;

    await this.fitMeasurementToViewInternal(provider, measurementId, viewers);
  }

  private async fitMeasurementToViewInternal(
    provider: MeasurementsCapabilityProvider,
    measurementId: string,
    viewers: Web3DViewer[]
  ): Promise<void> {
    const measurement = provider.items().find((m) => m.id === measurementId);
    if (!measurement) return;

    // Volumes are handled separately: boundary coordinates are read directly
    // from the measurement (no viewer markup timing dependency). When no
    // boundary exists the tileset model ID is used — the viewer handles any
    // async loading, mirroring the surface fit-to-view pattern.
    if (isVolumeMeasurement(measurement)) {
      if (measurement.hidden) return;

      const volumeBoundaryBox = getVolumeBoundaryBox(measurement);
      if (!volumeBoundaryBox && volumeUsesFilter(measurement)) return;

      const tilesetModelId =
        VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX + measurementId;

      const promises = volumeBoundaryBox
        ? viewers.map((viewer) =>
            viewer.camera.fitToView({
              box: volumeBoundaryBox,
              tightness: FIT_TO_VIEW_TIGHTNESS,
            })
          )
        : viewers.map((viewer) => viewer.camera.fitToView(tilesetModelId));

      await Promise.all(promises);
      return;
    }

    const promises = viewers.map(async (viewer) => {
      const markups = provider
        .getViewerMarkups(viewer, measurement)
        .filter((m) => m.visible);
      if (markups.length === 0) return;

      if (markups.length === 1 && markups[0].name === 'pointMarkup') {
        const cameraPosition = viewer.camera.position.clone();
        const rotationMatrix = new Matrix4();
        const targetPosition = markups[0].position.clone();

        rotationMatrix.lookAt(
          cameraPosition,
          targetPosition,
          new Vector3(0, 0, 1)
        );

        const targetQuaternion = new Quaternion();
        targetQuaternion.setFromRotationMatrix(rotationMatrix);

        await viewer.camera.animate(cameraPosition, targetQuaternion);
      } else {
        const models = new Map<string, number[]>([
          ['markup', markups.map((m) => m.id)],
        ]);

        await viewer.camera.fitToView({
          models,
          tightness: FIT_TO_VIEW_TIGHTNESS,
        });
      }
    });

    await Promise.all(promises);
  }

  public cleanup(): Promise<void> {
    return Promise.resolve();
  }
}
